/* eslint-disable react/jsx-no-bind */
/* eslint-disable jsx-a11y/media-has-caption */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Avatar,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
} from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import { useApolloClient, useQuery } from "@apollo/client";
import { GET_BROWSER_CALL_ROOM } from "src/graphql/query/browserCall/browserCallQueries";
import {
  JOIN_BROWSER_CALL_AS_GUEST,
  SEND_BROWSER_CALL_ICE_CANDIDATE,
  END_BROWSER_CALL,
  REPORT_BROWSER_CALL_GUEST_ISSUE,
} from "src/graphql/mutation/browserCall/browserCallMutations";
import { BROWSER_CALL_GUEST_SIGNALING } from "src/graphql/subscriptions/browserCall/browserCallSubscriptions";

type GuestState =
  | "loading"
  | "expired"
  | "unavailable"
  | "ready"
  | "joining"
  | "ringing"
  | "active"
  | "ended"
  | "error";

const formatDuration = (s: number) => {
  if (!s || Number.isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}:${ss.toString().padStart(2, "0")}`;
};

interface GuestErrorInfo {
  title: string;
  hint: string;
  retryable: boolean;
  // Allowlisted category sent to the backend for the agent's snackbar +
  // chat card. `null` means "don't notify the agent" (transient errors
  // like the user dismissing the mic prompt accidentally).
  code:
    | "no_microphone"
    | "permission_blocked"
    | "mic_in_use"
    | "mic_unsupported"
    | "insecure_context"
    | "generic_error"
    | null;
}

// Translate raw getUserMedia / RTCPeerConnection / Apollo errors into a
// title + hint the customer can act on. Default branch surfaces the raw
// message so we don't lie about the cause when we don't recognize the
// error code.
function mapGuestError(err: any): GuestErrorInfo {
  const name = String(err?.name || "");
  const rawMessage = String(err?.message || "");

  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return {
      title: "No microphone detected",
      hint: "We couldn't find a microphone on this device. Please connect a microphone, headset, or earbuds with a mic — then tap Retry. If you're on a desktop without one, try opening this link on your phone instead.",
      retryable: true,
      code: "no_microphone",
    };
  }
  if (name === "NotAllowedError" || name === "PermissionDeniedError") {
    return {
      title: "Microphone access blocked",
      hint: "Your browser is blocking microphone access. Tap the lock icon in the address bar, allow microphone, then tap Retry.",
      retryable: true,
      code: "permission_blocked",
    };
  }
  if (name === "NotReadableError" || name === "TrackStartError") {
    return {
      title: "Microphone is in use",
      hint: "Your microphone seems to be used by another app (like Zoom, Meet, or another call). Close other apps that might be using it, then tap Retry.",
      retryable: true,
      code: "mic_in_use",
    };
  }
  if (
    name === "OverconstrainedError" ||
    name === "ConstraintNotSatisfiedError"
  ) {
    return {
      title: "Microphone unsupported",
      hint: "Your microphone doesn't support the required audio settings. Try a different microphone or browser.",
      retryable: true,
      code: "mic_unsupported",
    };
  }
  if (name === "SecurityError") {
    return {
      title: "Insecure connection",
      hint: "Microphone access requires a secure (HTTPS) page. Please open the link in a modern browser or contact the agent for an updated link.",
      retryable: false,
      code: "insecure_context",
    };
  }
  if (name === "AbortError") {
    // Transient — customer dismissed the prompt by accident or browser
    // killed it on tab switch. Don't notify the agent; they'd just see
    // noise. Customer can simply tap Retry.
    return {
      title: "Microphone request was cancelled",
      hint: "The browser cancelled the microphone prompt. Tap Retry to try again.",
      retryable: true,
      code: null,
    };
  }
  return {
    title: "Couldn't start the call",
    hint:
      rawMessage ||
      "Something went wrong setting up the call. Please check your internet connection and tap Retry.",
    retryable: true,
    code: "generic_error",
  };
}

const GuestCallRoom: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const client = useApolloClient();

  const [state, setState] = useState<GuestState>("loading");
  const [errorMsg, setErrorMsg] = useState<string>("");
  // Friendly hint shown below the title in the error state. Populated by
  // mapGuestError() so the customer gets actionable guidance (e.g. "plug
  // in a mic") instead of the raw browser error string.
  const [errorHint, setErrorHint] = useState<string>("");
  const [errorRetryable, setErrorRetryable] = useState<boolean>(false);
  // True once we've sent a guest-issue report to the agent for this
  // session. Drives the "Agent has been notified" disclosure shown below
  // the error hint card.
  const [agentNotified, setAgentNotified] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [guestNameInput, setGuestNameInput] = useState("");

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const subUnsubRef = useRef<(() => void) | null>(null);
  const cleanupRanRef = useRef(false);
  // True iff the peer connection ever reached "connected" — see
  // AgentCallPopout for rationale.
  const mediaConnectedRef = useRef(false);
  // Tracks whether we already fired a guest-issue report for this
  // session, so that retries on the same room don't spam the agent's
  // snackbar with the same error each time.
  const reportedIssueCodeRef = useRef<string | null>(null);

  const {
    data: roomData,
    loading: roomLoading,
    error: roomError,
  } = useQuery(GET_BROWSER_CALL_ROOM, {
    variables: { roomId },
    skip: !roomId,
    fetchPolicy: "network-only",
  });

  const room = roomData?.getBrowserCallRoom;

  useEffect(() => {
    if (roomError) {
      setErrorMsg(roomError.message || "Could not load this call.");
      setState("error");
      return;
    }
    if (roomLoading) return;
    if (!room) {
      setState("unavailable");
      return;
    }
    if (room.status === "expired") {
      setState("expired");
    } else if (
      room.status === "completed" ||
      room.status === "cancelled" ||
      room.status === "missed" ||
      room.status === "failed"
    ) {
      setState("unavailable");
    } else if (room.status === "pending") {
      setState("ready");
    } else if (room.status === "ringing") {
      setState("ringing");
    } else if (room.status === "active") {
      // A different device joined.
      setState("unavailable");
    }
  }, [room, roomLoading, roomError]);

  const cleanup = useCallback(() => {
    if (cleanupRanRef.current) return;
    cleanupRanRef.current = true;
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach((t) => t.stop());
      remoteStreamRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.onicecandidate = null;
      pcRef.current.ontrack = null;
      pcRef.current.onconnectionstatechange = null;
      try {
        pcRef.current.close();
      } catch {
        /* ignore */
      }
      pcRef.current = null;
    }
    if (subUnsubRef.current) {
      subUnsubRef.current();
      subUnsubRef.current = null;
    }
  }, []);

  const endCall = useCallback(
    async (reason: string) => {
      if (!roomId) return;
      setState("ended");
      try {
        await client.mutate({
          mutation: END_BROWSER_CALL,
          variables: {
            roomId,
            reason,
            mediaConnected: mediaConnectedRef.current,
          },
        });
      } catch {
        /* ignore */
      }
      cleanup();
    },
    [client, cleanup, roomId],
  );

  useEffect(() => {
    const handler = () => {
      if (state === "active" || state === "ringing") {
        try {
          client
            .mutate({
              mutation: END_BROWSER_CALL,
              variables: {
                roomId,
                reason: "guest_window_closed",
                mediaConnected: mediaConnectedRef.current,
              },
            })
            .catch(() => {});
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [client, roomId, state]);

  const waitForIceComplete = (pc: RTCPeerConnection) =>
    new Promise<void>((resolve) => {
      if (pc.iceGatheringState === "complete") {
        resolve();
        return;
      }
      const check = () => {
        if (pc.iceGatheringState === "complete") {
          pc.removeEventListener("icegatheringstatechange", check);
          resolve();
        }
      };
      pc.addEventListener("icegatheringstatechange", check);
      // Hard cap so a single missing candidate doesn't stall forever.
      setTimeout(() => {
        pc.removeEventListener("icegatheringstatechange", check);
        resolve();
      }, 2500);
    });

  const join = useCallback(async () => {
    if (!roomId || !room) return;
    setState("joining");
    try {
      const iceServers: RTCIceServer[] = (room.iceServers || []).map(
        (s: any) => ({
          urls: s.urls,
          username: s.username || undefined,
          credential: s.credential || undefined,
        }),
      );

      const local = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
      localStreamRef.current = local;

      const pc = new RTCPeerConnection({ iceServers });
      pcRef.current = pc;

      local.getTracks().forEach((track) => pc.addTrack(track, local));

      const remote = new MediaStream();
      remoteStreamRef.current = remote;

      pc.ontrack = (ev) => {
        ev.streams[0]?.getTracks().forEach((t) => remote.addTrack(t));
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = remote;
          remoteAudioRef.current.play().catch(() => {});
        }
      };

      pc.onicecandidate = (ev) => {
        if (ev.candidate) {
          client
            .mutate({
              mutation: SEND_BROWSER_CALL_ICE_CANDIDATE,
              variables: {
                roomId,
                candidate: JSON.stringify(ev.candidate.toJSON()),
                fromRole: "guest",
              },
            })
            .catch(() => {});
        }
      };

      pc.onconnectionstatechange = () => {
        const st = pc.connectionState;
        if (st === "connected") {
          mediaConnectedRef.current = true;
          setState("active");
          if (startedAtRef.current == null) {
            startedAtRef.current = Date.now();
            tickRef.current = setInterval(() => {
              if (startedAtRef.current) {
                setDuration(
                  Math.round((Date.now() - startedAtRef.current) / 1000),
                );
              }
            }, 500);
          }
        } else if (st === "failed" || st === "disconnected") {
          endCall("connection_failed");
        }
      };

      // Subscribe to agent-side signaling for this room.
      const obs = client.subscribe({
        query: BROWSER_CALL_GUEST_SIGNALING,
        variables: { roomId },
      });
      const sub = obs.subscribe({
        next: async ({ data }) => {
          const payload = data?.browserCallGuestSignaling;
          if (!payload) return;
          if (payload.event === "agent_accepted" && payload.sdp) {
            try {
              await pc.setRemoteDescription(
                new RTCSessionDescription({ type: "answer", sdp: payload.sdp }),
              );
            } catch {
              /* ignore */
            }
          } else if (payload.event === "ice_candidate" && payload.candidate) {
            try {
              const cand = JSON.parse(payload.candidate);
              pc.addIceCandidate(new RTCIceCandidate(cand)).catch(() => {});
            } catch {
              /* ignore */
            }
          } else if (
            payload.event === "agent_rejected" ||
            payload.event === "agent_cancelled" ||
            payload.event === "ended"
          ) {
            setState("ended");
            cleanup();
          }
        },
        error: () => {
          /* ignore */
        },
      });
      subUnsubRef.current = () => sub.unsubscribe();

      // Build a complete offer (waits for ICE) so the agent gets every
      // candidate inline — eliminates the early-ICE race window.
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
      });
      await pc.setLocalDescription(offer);
      await waitForIceComplete(pc);

      const sdpToSend = pc.localDescription?.sdp || offer.sdp || "";

      const finalGuestName =
        room.customerName ||
        (guestNameInput.trim() ? guestNameInput.trim() : null);

      await client.mutate({
        mutation: JOIN_BROWSER_CALL_AS_GUEST,
        variables: {
          roomId,
          sdpOffer: sdpToSend,
          guestName: finalGuestName,
        },
      });

      setState("ringing");
    } catch (err: any) {
      // Map the raw browser/Apollo error into a friendly title + hint so
      // the customer sees something they can act on instead of a cryptic
      // "Requested device not found".
      const info = mapGuestError(err);
      setErrorMsg(info.title);
      setErrorHint(info.hint);
      setErrorRetryable(info.retryable);
      setState("error");
      cleanup();
      // Reset the cleanup-guard so a Retry can reinitialize peer
      // connection + streams.
      cleanupRanRef.current = false;

      // Fire-and-forget: notify the agent so they can text the customer
      // instead of waiting blind. Skip:
      //  - null codes (AbortError — transient, not worth a snackbar)
      //  - codes we've already reported this session (avoids retry spam
      //    of the same error code; a *different* code on the next retry
      //    still goes through)
      if (info.code && reportedIssueCodeRef.current !== info.code) {
        reportedIssueCodeRef.current = info.code;
        client
          .mutate({
            mutation: REPORT_BROWSER_CALL_GUEST_ISSUE,
            variables: {
              roomId,
              code: info.code,
              detail: String(err?.message || "").slice(0, 500),
            },
          })
          .then(() => setAgentNotified(true))
          .catch(() => {
            /* don't surface — the customer-facing error already covers it */
          });
      }
    }
  }, [room, roomId, client, cleanup, endCall, guestNameInput]);

  // Retry — re-arm room state and re-run join(). Used by the error UI's
  // "Try again" button after the user fixes their mic / permission.
  const retryJoin = useCallback(() => {
    setErrorMsg("");
    setErrorHint("");
    setErrorRetryable(false);
    setAgentNotified(false);
    setState("ready");
    // Defer the actual join one tick so React commits the "ready" state
    // before we mutate it again.
    setTimeout(() => {
      join();
    }, 0);
  }, [join]);

  const toggleMute = useCallback(() => {
    if (!localStreamRef.current) return;
    const next = !isMuted;
    localStreamRef.current.getAudioTracks().forEach((t) => {
      t.enabled = !next;
    });
    setIsMuted(next);
  }, [isMuted]);

  // Tear down on unmount.
  useEffect(() => () => cleanup(), [cleanup]);

  const heading = useMemo(() => {
    if (room?.businessName) return room.businessName;
    return "Live Call";
  }, [room]);

  const greetingName = useMemo(() => {
    if (room?.customerName) return room.customerName.split(" ")[0];
    return "there";
  }, [room]);

  const statusLine = useMemo(() => {
    switch (state) {
      case "loading":
        return "Loading call...";
      case "expired":
        return "This call link has expired.";
      case "unavailable":
        return "This call link is no longer available.";
      case "ready":
        return `Hi ${greetingName}! ${
          room?.agentDisplayName || "An agent"
        } is ready to talk.`;
      case "joining":
        return "Connecting your microphone...";
      case "ringing":
        return "Calling... please wait for the agent to answer.";
      case "active":
        return `In call · ${formatDuration(duration)}`;
      case "ended":
        return "Call ended.";
      case "error":
        return errorMsg || "Something went wrong.";
      default:
        return "";
    }
  }, [state, errorMsg, duration, greetingName, room]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg,#0f172a,#1e293b)",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Box sx={{ textAlign: "center", maxWidth: 360, width: "100%" }}>
        <Typography sx={{ fontSize: 14, opacity: 0.75, mb: 0.5 }}>
          {heading}
        </Typography>
        <Avatar
          sx={{
            width: 96,
            height: 96,
            bgcolor: "#2563eb",
            mx: "auto",
            my: 2,
            fontSize: 36,
          }}
        >
          {(room?.agentDisplayName || "A").slice(0, 1).toUpperCase()}
        </Avatar>
        <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
          {room?.agentDisplayName || "Agent"}
        </Typography>
        <Typography sx={{ fontSize: 13, opacity: 0.75, mt: 0.5 }}>
          {room?.mediaMode === "video" ? "Video" : "Audio"} call
        </Typography>

        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "center",
            gap: 1,
            alignItems: "center",
          }}
        >
          {(state === "loading" || state === "joining") && (
            <CircularProgress size={18} sx={{ color: "#fff" }} />
          )}
          <Typography sx={{ fontSize: 14 }}>{statusLine}</Typography>
        </Box>

        {state === "ready" && !room?.customerName && (
          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Your name (optional)"
              value={guestNameInput}
              onChange={(e) => setGuestNameInput(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  background: "rgba(255,255,255,0.07)",
                  color: "#fff",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(255,255,255,0.2)",
                },
              }}
            />
          </Box>
        )}

        {state === "ready" && (
          <Button
            variant="contained"
            startIcon={<CallIcon />}
            onClick={join}
            sx={{
              mt: 3,
              bgcolor: "#16a34a",
              "&:hover": { bgcolor: "#15803d" },
              borderRadius: 20,
              px: 4,
              py: 1.25,
            }}
          >
            Join Call
          </Button>
        )}

        {(state === "ringing" || state === "active") && (
          <Box
            sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center" }}
          >
            <Tooltip title={isMuted ? "Unmute mic" : "Mute mic"}>
              <span>
                <IconButton
                  onClick={toggleMute}
                  disabled={state !== "active"}
                  sx={{
                    bgcolor: isMuted ? "#dc2626" : "rgba(255,255,255,0.1)",
                    color: "#fff",
                    "&:hover": {
                      bgcolor: isMuted ? "#b91c1c" : "rgba(255,255,255,0.2)",
                    },
                  }}
                >
                  {isMuted ? <MicOffIcon /> : <MicIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Hang up">
              <IconButton
                onClick={() => endCall("guest_hangup")}
                sx={{
                  bgcolor: "#dc2626",
                  color: "#fff",
                  width: 56,
                  height: 56,
                  "&:hover": { bgcolor: "#b91c1c" },
                }}
              >
                <CallEndIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {state === "ended" && (
          <Typography sx={{ mt: 4, fontSize: 12, opacity: 0.6 }}>
            You can close this window.
          </Typography>
        )}

        {state === "error" && (
          <Box sx={{ mt: 3 }}>
            {errorHint && (
              <Typography
                sx={{
                  fontSize: 13,
                  opacity: 0.82,
                  lineHeight: 1.5,
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  textAlign: "left",
                }}
              >
                {errorHint}
              </Typography>
            )}
            {agentNotified && (
              <Typography
                sx={{
                  mt: 1.5,
                  fontSize: 12,
                  opacity: 0.7,
                  textAlign: "left",
                  pl: 0.5,
                }}
              >
                ✓ The agent has been notified — they may reach out to you on
                chat.
              </Typography>
            )}
            {errorRetryable && (
              <Button
                variant="contained"
                startIcon={<CallIcon />}
                onClick={retryJoin}
                sx={{
                  mt: 3,
                  bgcolor: "#2563eb",
                  "&:hover": { bgcolor: "#1d4ed8" },
                  borderRadius: 20,
                  px: 4,
                  py: 1.25,
                }}
              >
                Try again
              </Button>
            )}
          </Box>
        )}
      </Box>

      <audio ref={remoteAudioRef} autoPlay hidden />
    </Box>
  );
};

export default GuestCallRoom;
